import { Panel } from './ui/Controls';
import { store, useStore } from '@/state/store';

/** Layer list: select, hide, lock, and reorder the pieces of the design. */
export function LayerPanel() {
  const spec = useStore((s) => s.variants.find((v) => v.id === s.activeId) ?? null);
  const selectedId = useStore((s) => s.selectedBlockId);
  const hasLogo = useStore((s) => Boolean(s.logoDataUrl));

  if (!spec) {
    return (
      <Panel title="Layers">
        <p className="text-[11px] text-ink-500">Generate a design to see its layers.</p>
      </Panel>
    );
  }

  const move = (id: string, direction: -1 | 1) => {
    store.updateActive((current) => {
      const index = current.blocks.findIndex((b) => b.id === id);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= current.blocks.length) return current;
      const blocks = current.blocks.slice();
      [blocks[index], blocks[target]] = [blocks[target], blocks[index]];
      return { ...current, blocks };
    });
  };

  return (
    <Panel title="Layers">
      <ul className="space-y-1">
        {hasLogo && spec.logo && (
          <li className="flex items-center gap-1.5 rounded-md bg-white/[0.03] px-2 py-1.5">
            <span className="flex-1 truncate text-[11px] text-ink-200">Logo</span>
            <IconButton
              label={spec.logo.visible ? 'Hide logo' : 'Show logo'}
              active={spec.logo.visible}
              onClick={() =>
                store.updateActive((current) => ({
                  ...current,
                  logo: current.logo
                    ? { ...current.logo, visible: !current.logo.visible }
                    : null,
                }))
              }
            >
              {spec.logo.visible ? '◉' : '○'}
            </IconButton>
          </li>
        )}

        {[...spec.blocks].reverse().map((block) => (
          <li
            key={block.id}
            className={`flex items-center gap-1 rounded-md px-2 py-1.5 transition ${
              selectedId === block.id
                ? 'bg-accent/15 ring-1 ring-accent/35'
                : 'bg-white/[0.03] hover:bg-white/[0.06]'
            }`}
          >
            <button
              type="button"
              onClick={() => store.set({ selectedBlockId: block.id })}
              className="min-w-0 flex-1 text-left"
            >
              <span className="block truncate text-[11px] text-ink-200">
                {block.text || `(empty ${block.role})`}
              </span>
              <span className="block text-[9px] capitalize text-ink-500">
                {block.role} · {block.lines.length} line{block.lines.length > 1 ? 's' : ''}
              </span>
            </button>
            <IconButton label="Move up" onClick={() => move(block.id, 1)}>
              ↑
            </IconButton>
            <IconButton label="Move down" onClick={() => move(block.id, -1)}>
              ↓
            </IconButton>
            <IconButton
              label={block.locked ? 'Unlock' : 'Lock'}
              active={block.locked}
              onClick={() =>
                store.updateActive((current) => ({
                  ...current,
                  blocks: current.blocks.map((b) =>
                    b.id === block.id ? { ...b, locked: !b.locked } : b,
                  ),
                }))
              }
            >
              {block.locked ? '🔒' : '🔓'}
            </IconButton>
            <IconButton
              label={block.visible ? 'Hide' : 'Show'}
              active={block.visible}
              onClick={() =>
                store.updateActive((current) => ({
                  ...current,
                  blocks: current.blocks.map((b) =>
                    b.id === block.id ? { ...b, visible: !b.visible } : b,
                  ),
                }))
              }
            >
              {block.visible ? '◉' : '○'}
            </IconButton>
          </li>
        ))}

        {spec.overlays.map((overlay, index) => (
          <li
            key={overlay.id}
            className="flex items-center gap-1.5 rounded-md bg-white/[0.02] px-2 py-1.5"
          >
            <span className="flex-1 truncate text-[11px] text-ink-400">
              {overlay.kind === 'panel'
                ? overlay.blur > 0
                  ? 'Blurred panel'
                  : 'Panel'
                : overlay.kind === 'linear'
                  ? 'Gradient scrim'
                  : overlay.kind === 'radial'
                    ? 'Radial scrim'
                    : 'Color wash'}
            </span>
            <IconButton
              label={overlay.visible ? 'Hide overlay' : 'Show overlay'}
              active={overlay.visible}
              onClick={() =>
                store.updateActive((current) => ({
                  ...current,
                  overlays: current.overlays.map((ov, i) =>
                    i === index ? { ...ov, visible: !ov.visible } : ov,
                  ),
                }))
              }
            >
              {overlay.visible ? '◉' : '○'}
            </IconButton>
          </li>
        ))}

        <li className="flex items-center gap-1.5 rounded-md bg-white/[0.02] px-2 py-1.5">
          <span className="flex-1 truncate text-[11px] text-ink-400">Photo</span>
          <span className="text-[9px] text-ink-600">base</span>
        </li>
      </ul>
    </Panel>
  );
}

function IconButton({
  children,
  label,
  onClick,
  active = true,
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={`rounded px-1 py-0.5 text-[10px] transition hover:bg-white/10 ${
        active ? 'text-ink-300' : 'text-ink-600'
      }`}
    >
      {children}
    </button>
  );
}
